import { shrinePopName } from "./shrine-pair.ts";
import { CHEST, DUMMY, GATE, LANTERN, ROOF, SHRINE, SIGN, WAYPOINT, WELL } from "./type-names.ts";

/**
 * Suggesting the frame of a further state from the placed frame's name (ADR-053 decision 2). The packs name
 * a thing's states as suffixes of one base name (`waterwell - rope` beside `waterwell - no rope`), so a
 * suggestion is a rewrite of the placed frame's name, which the editor then resolves against the catalogue.
 * A well stands lowered on the placed frame (ADR-042's amendment of 2026-10-03): placed on the still frame
 * without its rope it rests raised, full or empty, on the frame with it, since that sheet draws no water. A
 * well placed on a bucket strip is suggested nothing: its raised states rest on a strip's last frame, which
 * only the transition chooser's offer can name.
 * A gate's two strips (`fence-gate-1-opening`, `fence-gate-1-closing`) start on the closed gate and on the
 * open gate, so the closing strip's name is the open gate and the opening strip's the closed one. The opening
 * strip ends on the closing strip's second drawing, not its first (ADR-058, P1.8-T6 note 4). A roof's
 * strips (`cabin-top section with tissue-opening animation`) pair the same way; the cabin's closing strip
 * starts on the frame the artist left blank, the roof fully gone, and the chooser's offer rests it open on
 * the opening strip's last frame instead (ADR-058 amendment, P1.3-T4). A training dummy's struck states rest on
 * the frame it stands on, which every hit strip starts and ends on (ADR-058's amendment for P2.2-T7), so their
 * suggestion is the placed name itself. A chest's strips pair as a gate's do, but the packs put the word in the
 * middle of the name as often as at its end (`chests-opening-no horns`, `chest-opening - lock 2`,
 * `buried chest-opening-gold`), so a chest's rule rewrites the word wherever it stands (ADR-091 decision 17).
 * A shrine placed on its loop rests `dormant` on its obelisk's still, the loop's name with "-buff available
 * animation" replaced by "-still" wherever it stands (ADR-096's amendment of 2026-10-05, item 6); where the tileset
 * lists no such frame the editor resolves nothing and the transition chooser's last-frame offer is the way. Its `pop`
 * is suggested by the pairing of the two strips by their words, `shrinePopName` (ADR-096 decisions 4 and 40), through
 * `suggestAnimationName`. A lantern stands unlit on the placed frame and rests lit on the frame of the same name with
 * ` - lit` after it (`lamp 1` beside `lamp 1 - lit`); a pack that names its lit frame otherwise resolves to no frame
 * and its author names it. The rules are the game's, never Daedalus's.
 */

/**
 * One state's naming rule: what the placed frame's name must end with and what the state's name ends with
 * instead; with `anywhere`, what the placed name must hold and what the state's name holds in its place.
 */
interface NameRule {
  readonly from: string;
  readonly to: string;
  readonly anywhere?: true;
}

const RULES: ReadonlyMap<string, ReadonlyMap<string, NameRule>> = new Map([
  [WELL, new Map([["raised", { from: " - no rope", to: " - rope" }], ["raisedEmpty", { from: " - no rope", to: " - rope" }]])],
  [SIGN, new Map<string, NameRule>()],
  [GATE, new Map([["open", { from: "-opening", to: "-closing" }], ["closed", { from: "-closing", to: "-opening" }]])],
  [ROOF, new Map([["open", { from: "-opening animation", to: "-closing animation" }], ["closed", { from: "-closing animation", to: "-opening animation" }]])],
  [DUMMY, new Map([["struck", { from: "", to: "" }], ["struck-again", { from: "", to: "" }]])],
  [CHEST, new Map<string, NameRule>([["open", { from: "-opening", to: "-closing", anywhere: true }], ["closed", { from: "-closing", to: "-opening", anywhere: true }]])],
  [SHRINE, new Map<string, NameRule>([["dormant", { from: "-buff available animation", to: "-still", anywhere: true }]])],
  [WAYPOINT, new Map<string, NameRule>([["dormant", { from: "-buff available animation", to: "-still", anywhere: true }]])],
  [LANTERN, new Map([["lit", { from: "", to: " - lit" }]])],
]);

/**
 * The frame name a state should show, given the name of the frame the author placed.
 *
 * @param type The object type id.
 * @param stateId The state wanting a frame.
 * @param placedName The name of the frame the prop carries, as the catalogue lists it.
 * @returns The suggested frame name, or undefined when the game has no rule for the state or the placed
 *   name does not fit the one it has.
 */
export function suggestFrameName(type: string, stateId: string, placedName: string): string | undefined {
  const rule = RULES.get(type)?.get(stateId);
  if (rule === undefined) {
    return undefined;
  }
  if (rule.anywhere === true) {
    const at = placedName.lastIndexOf(rule.from);
    return at < 0 ? undefined : `${placedName.slice(0, at)}${rule.to}${placedName.slice(at + rule.from.length)}`;
  }
  if (!placedName.endsWith(rule.from)) {
    return undefined;
  }
  return `${placedName.slice(0, placedName.length - rule.from.length)}${rule.to}`;
}

/**
 * Every frame name any registered type would suggest for some state of a prop placed on a frame of this name:
 * the index of a pack uses it to bring a prop's states with a pull (ADR-129 decision 3.9), when no type has been
 * chosen yet.
 *
 * @param placedName The name of the frame, as the catalogue lists it.
 * @returns The suggested names, each once, in the order the types are registered; none where no rule fits.
 */
export function suggestedFrameNames(placedName: string): string[] {
  const found = new Set<string>();
  for (const [type, states] of RULES) {
    for (const state of states.keys()) {
      const name = suggestFrameName(type, state, placedName);
      if (name !== undefined) {
        found.add(name);
      }
    }
  }
  return [...found];
}

/**
 * The strip an animation of a type should play, given the name of the frame the author placed and the names of that
 * frame's tileset (ADR-096 decision 40): a shrine's and a waypoint's `pop` is the "getting buff" strip its loop pairs with. No other
 * type is suggested one: a gate's and a chest's strips are chosen by hand.
 *
 * @param type The object type id.
 * @param animation The animation's name, a transition's `plays`.
 * @param placedName The name of the frame the prop carries, as the catalogue lists it.
 * @param names The names of the placed frame's tileset.
 * @returns The strip's name, or undefined when the game has no rule for the animation or no listed name fits.
 */
export function suggestAnimationName(type: string, animation: string, placedName: string, names: readonly string[]): string | undefined {
  return (type === SHRINE || type === WAYPOINT) && animation === "pop" ? shrinePopName(placedName, names) : undefined;
}
